import React, { useState, useEffect, useCallback } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase-config.js";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

const COLORS = {
  Party: "#FF6B6B",
  Chill: "#2e6db5",     // Muuzbox blue
  Energetic: "#FFE66D",
  Romantic: "#fa163f",  // Muuzbox red
};

const MoodDashboard = ({ clientObj }) => {
  const [moodData, setMoodData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalMoods, setTotalMoods] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMoodData = useCallback(async () => {
    if (!clientObj?.clientId) {
      setLoading(false);
      return;
    }

    console.log('clientObj', clientObj);

    try {
      // Query crowdmood collection for venue's clientId
      const moodQuery = query(
        collection(db, "crowdmood"),
        where("clientId", "==", clientObj.clientId)
      );

      const querySnapshot = await getDocs(moodQuery);

      // Count moods
      const moodCounts = {};
      querySnapshot.forEach((doc) => {
        const mood = doc.data().mood;
        moodCounts[mood] = (moodCounts[mood] || 0) + 1;
      });

      // Format data for recharts
      const formattedData = Object.entries(moodCounts).map(
        ([mood, count]) => ({
          name: mood,
          value: count,
          percentage: ((count / querySnapshot.size) * 100).toFixed(1),
        })
      );

      setMoodData(formattedData);
      setTotalMoods(querySnapshot.size);
    } catch (error) {
      console.error("Error fetching mood data:", error);
    }

    setLoading(false);
  }, [clientObj?.clientId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMoodData();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchMoodData();
  }, [fetchMoodData]);


  if (loading) {
    return (
      <div className="rounded-2xl bg-gray-100 h-full p-4 panel-fade">
        <p className="text-gray-500 text-center">Loading mood data...</p>
      </div>
    );
  }

  if (moodData.length === 0) {
    return (
      <div className="rounded-2xl bg-gray-100 h-full p-4 panel-fade">
        <p className="text-gray-600 text-center">No mood data available yet</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-gray-100 h-full p-4 panel-fade">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-bold text-gray-800 flex-1 text-center">
          Crowd Mood
        </h3>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className={`p-2 rounded-full transition-all ${
            refreshing 
              ? "bg-gray-300 cursor-not-allowed" 
              : "bg-white hover:bg-gray-200 cursor-pointer"
          }`}
          title="Refresh mood data"
        >
          <svg
            className={`w-4 h-4 text-gray-600 ${refreshing ? "animate-spin" : ""}`}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
      <p className="text-sm text-gray-600 text-center mb-4">
        Total responses: {totalMoods}
      </p>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={moodData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis 
            dataKey="name" 
            tick={{ fill: '#666' }}
            axisLine={{ stroke: '#666' }}
          />
          <YAxis 
            tick={{ fill: '#666' }}
            axisLine={{ stroke: '#666' }}
            label={{ value: 'Votes', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip
            formatter={(value, name) => [`${value} votes`, name]}
            contentStyle={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              border: "1px solid #ccc",
              borderRadius: "8px",
            }}
          />
          <Bar dataKey="value" radius={[8, 8, 0, 0]}>
            {moodData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[entry.name] || "#8884d8"}
              />
            ))}
          </Bar>
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value, entry) =>
              `${value} (${entry.payload.percentage}%)`
            }
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MoodDashboard;
